import { useSlotId } from '@react-aria/utils';
import type { CheckboxProps } from '@react-types/checkbox';
import clsx from 'clsx';
import * as React from 'react';
import { useCheckboxGroupItem, VisuallyHidden } from 'react-aria';

import { assert } from '@gitbook/util-ts';

import { useCheckboxGroupContext } from '../Checkbox';
import { SegmentedControlContextProvider } from '../SegmentedControlContext';
import { CardProvider } from './Card';
import { useCardButton } from './CardButton';

export type CardCheckboxProps = CheckboxProps & {
    className?: string;
    cardHighlight?: string;
    value: string;
} & Pick<React.ComponentPropsWithoutRef<'label'>, 'onMouseMove'>;

/**
 * Card checkbox component to create a checkbox with a card layout.
 */
export const CardCheckbox = React.forwardRef(function CardCheckbox(
    { className, ...props }: CardCheckboxProps,
    forwardedRef: React.ForwardedRef<HTMLLabelElement>
) {
    const state = useCheckboxGroupContext();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { value } = props;
    assert(value, 'Value is required');
    const { inputProps, isSelected, isDisabled } = useCheckboxGroupItem(props, state, inputRef);
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const inputId = useSlotId();
    const cardProps = useCardButton({
        elementType: 'label',
        disabled: isDisabled,
        headingId,
        descriptionId,
    });
    const ctxValue = React.useMemo(() => ({ isSelected }), [isSelected]);

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            {/* biome-ignore lint/a11y/useKeyWithMouseEvents: already focusable */}
            <label
                {...cardProps}
                ref={forwardedRef}
                htmlFor={inputId}
                aria-labelledby={headingId}
                aria-describedby={descriptionId}
                className={clsx(
                    className,
                    cardProps.className,
                    'relative transition',
                    isSelected && 'border-primary',
                    state.isInvalid && 'border-control-invalid'
                )}
                onMouseMove={props.onMouseMove}
            >
                <VisuallyHidden>
                    <input {...inputProps} ref={inputRef} id={inputId} />
                </VisuallyHidden>
                <SegmentedControlContextProvider value={ctxValue}>
                    {props.children}
                </SegmentedControlContextProvider>
            </label>
        </CardProvider>
    );
});
